import {Base64} from 'js-base64';

function getBaseUrl(): string {
  const raw =
    process.env.VUE_APP_ADMIN_API_BASE_URL || 'http://localhost:31109/';
  return raw.endsWith('/') ? raw : raw + '/';
}

function getBasicAuthHeader(): string {
  const username = process.env.VUE_APP_ADMIN_API_BASIC_AUTH_USERNAME || '';
  const password = process.env.VUE_APP_ADMIN_API_BASIC_AUTH_PASSWORD || '';
  const token = Base64.encode(`${username}:${password}`);
  return `Basic ${token}`;
}

async function getJson<T>(
  path: string,
  query?: Record<string, any>,
): Promise<T> {
  const url = new URL(path.replace(/^\//, ''), getBaseUrl());
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      url.searchParams.set(k, String(v));
    });
  }

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: getBasicAuthHeader(),
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Admin API error ${res.status}: ${text || res.statusText}`);
  }

  return (await res.json()) as T;
}

async function postWithQuery<T>(
  path: string,
  query?: Record<string, any>,
): Promise<T> {
  const url = new URL(path.replace(/^\//, ''), getBaseUrl());
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      url.searchParams.set(k, String(v));
    });
  }

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: getBasicAuthHeader(),
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Admin API error ${res.status}: ${text || res.statusText}`);
  }

  return (await res.json()) as T;
}

async function getBlob(
  path: string,
  query?: Record<string, any>,
  accept?: string,
): Promise<Blob> {
  const url = new URL(path.replace(/^\//, ''), getBaseUrl());
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      url.searchParams.set(k, String(v));
    });
  }

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Accept: accept || 'application/octet-stream',
      Authorization: getBasicAuthHeader(),
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Admin API error ${res.status}: ${text || res.statusText}`);
  }

  return await res.blob();
}

export type AdminReport = {
  id: string;
  label: string;
  description?: string;
  endpoint: string;
  exportEndpoint?: string;
  columns?: string[];
};

export type AdminScript = {
  id: string;
  label: string;
  description?: string;
  runEndpoint: string;
  params?: Record<string, any>;
};

export type CompanyStatusResponse = {
  ok: boolean;
  details?: {
    schema_id?: string;
    company_id?: string;
    designation?: string;
    verification_status?: string;
  };
};

export type CompanyBlockedStatusResponse = {
  ok: boolean;
  details?: {
    schema_id?: string;
    company_id?: string;
    designation?: string;
    blocked?: boolean;
  };
};

export type RegisteredCompaniesResponse = {
  rows: Array<Record<string, any>>;
  total: number;
  columns?: string[];
};

export type CompanyUsersResponse = {
  rows: Array<Record<string, any>>;
  total: number;
  columns?: string[];
};

export type CompanyOption = {
  id: string;
  schema_id?: string;
  designation?: string;
  vat_no?: string;
};

/** Max allowed by GET /reports/company-options (FastAPI Query le=1000). */
export const COMPANY_OPTIONS_MAX_LIMIT = 1000;

export type CompaniesNoLoginResponse = {
  rows: Array<Record<string, any>>;
  total: number;
  columns?: string[];
};

export type RegisteredUsersResponse = {
  rows: Array<Record<string, any>>;
  total: number;
  columns?: string[];
};

export type ModuleUsageMonthlyRow = {
  log_type: string;
  nb_utilisateurs: number;
  adoption_pct?: number;
  trend_pct?: number;
  utilisateurs: string;
};

export type ModuleUsageMonthlyResponse = {
  rows: ModuleUsageMonthlyRow[];
  total: number;
  columns?: string[];
};

export type ModuleUsageMonthlyKpisResponse = {
  total_unique_users: number;
  modules_used_count: number;
  total_events: number;
  top_module: string | null;
  top_module_users: number;
  avg_active_days_per_user?: number;
  trend_active_users_pct?: number;
};

export type SimpleOption = {
  id: string;
  label: string;
};

export type ModuleUsageDrilldownRow = {
  user_id: string;
  username_email: string;
  company_designation: string;
  active_days: number;
  last_seen: string;
};

export type ModuleUsageDrilldownResponse = {
  rows: ModuleUsageDrilldownRow[];
  total: number;
  columns?: string[];
};

export type CopyBaseDataPreviewResponse = {
  ok: boolean;
  hasExisting: boolean;
  existing: Record<
    string,
    {
      description: string;
      count: number;
    }
  >;
};

export type ApiKeyRow = {
  id: string;
  user_id: string;
  company_id: string;
  jwt_id?: string | null;
  active: boolean;
  schema_id: string;
  access_level: string;
  creation_date?: string | null;
  billable: boolean;
  price_per_month?: number | null;
  username_email: string;
  first_name?: string | null;
  last_name?: string | null;
};

export type ApiKeyUserOption = {
  id: string;
  username_email: string;
  first_name?: string | null;
  last_name?: string | null;
};

export type GenerateApiKeyPayload = {
  schema_id: string;
  username: string;
  access_level: 'ro' | 'rw';
  billable: boolean;
  price_per_month?: number | null;
};

export type GenerateApiKeyResponse = {
  ok: boolean;
  message?: string;
};

export type PatchApiKeyPayload = {
  active?: boolean;
  billable?: boolean;
  price_per_month?: number | null;
};

export type PatchApiKeyResponse = {
  ok: boolean;
  message?: string;
  details?: {
    id: string;
    active: boolean;
    billable: boolean;
    price_per_month: number;
  };
};

export type RevokeApiKeyResponse = {
  ok: boolean;
  message?: string;
  details?: {
    id: string;
    user_id: string;
    schema_id: string;
  };
};

export type ApiKeyTokenResponse = {
  ok: boolean;
  id: string;
  jwt_token: string;
  jwt_id?: string | null;
  active: boolean;
  schema_id: string;
  access_level: string;
  username_email: string;
  first_name?: string | null;
  last_name?: string | null;
};

export type CompanyServiceRow = {
  id: string;
  date: string;
  company_id: string;
  company_designation?: string | null;
  description?: string | null;
  duration_minutes: number;
  billable: boolean;
  user_id?: string | null;
  user_first_name?: string | null;
  user_last_name?: string | null;
  custom_data?: Record<string, unknown> | null;
  creation_date?: string | null;
  billed?: boolean;
};

export type CompanyServiceCreatePayload = {
  date: string;
  company_id: string;
  description?: string | null;
  duration_minutes: number;
  billable?: boolean;
  user_id?: string | null;
  custom_data?: Record<string, unknown>;
};

export type CompanyServicePatchPayload = {
  date?: string;
  description?: string | null;
  duration_minutes?: number;
  billable?: boolean;
  user_id?: string | null;
  custom_data?: Record<string, unknown>;
};

export type LicenceRow = {
  id: string;
  user_count: number | null;
  active: boolean | null;
};

export type LicenceModuleRow = {
  id: string;
  licence_id: string;
  module_id: string;
  designation_key?: string | null;
  icon_key?: string | null;
};

export type LicenceCompanyRow = {
  id: string;
  licence_id: string;
  company_id: string;
  main?: boolean | null;
  designation?: string | null;
};

export type LicenceDetails = LicenceRow & {
  modules: LicenceModuleRow[];
  companies: LicenceCompanyRow[];
};

export type LicenceCompaniesPayload = {
  company_ids: string[];
  main_company_id?: string | null;
};

export type UsageHistoryPeriodRow = {
  period: string;
  period_label: string;
  total_amount: number;
  module_amount: number;
  peppol_amount: number;
  company_service_amount: number;
  nsso_checkin_quantity: number;
  nsso_sms_quantity: number;
  billed_companies_count: number;
  billed_users_count: number;
  distinct_modules_count: number;
  mom_amount_change_pct: number | null;
};

export type UsageTopModuleRow = {
  module_id: string;
  designation_key: string;
  amount: number;
  quantity: number;
};

export type UsageHistoryResponse = {
  months: number;
  company_id: string | null;
  periods: UsageHistoryPeriodRow[];
  summary: {
    latest_period: string | null;
    latest_total_amount: number;
    avg_monthly_amount: number;
    avg_billed_companies: number;
    latest_billed_users_count: number;
    latest_distinct_modules_count: number;
    latest_nsso_checkin_quantity: number;
    latest_nsso_sms_quantity: number;
    latest_mom_amount_change_pct: number | null;
  };
  top_modules_latest: UsageTopModuleRow[];
};

export type UsageForecastResponse = {
  period: string;
  period_label: string;
  period_start: string;
  period_end: string;
  computed_at: string | null;
  company_id: string | null;
  current_amount: number;
  previous_period: string;
  previous_period_label: string;
  previous_amount: number;
  variation_vs_previous_pct: number | null;
  amount_by_type: {
    module: number;
    peppol: number;
    company_service: number;
  };
  billed_companies_count: number;
  billed_users_count: number;
  previous_billed_users_count: number;
  distinct_modules_count: number;
  nsso_checkin_quantity: number;
  previous_nsso_checkin_quantity: number;
  nsso_sms_quantity: number;
  previous_nsso_sms_quantity: number;
  arpu: number;
  top_modules: UsageTopModuleRow[];
};

async function patchJson<T>(
  path: string,
  body?: Record<string, any>,
): Promise<T> {
  const url = new URL(path.replace(/^\//, ''), getBaseUrl());
  const res = await fetch(url.toString(), {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Admin API error ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as T;
}

async function deleteJson<T>(path: string): Promise<T> {
  const url = new URL(path.replace(/^\//, ''), getBaseUrl());
  const res = await fetch(url.toString(), {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      Authorization: getBasicAuthHeader(),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Admin API error ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as T;
}

export const adminApi = {
  listReports(): Promise<AdminReport[]> {
    return getJson<AdminReport[]>('/reports');
  },
  listScripts(): Promise<AdminScript[]> {
    return getJson<AdminScript[]>('/scripts');
  },
  getCompanyVerificationStatus(
    schemaId: string,
  ): Promise<CompanyStatusResponse> {
    return getJson<CompanyStatusResponse>(
      '/scripts/update_company_status/status',
      {
        schema_id: schemaId,
      },
    );
  },
  getCompanyBlockedStatus(
    schemaId: string,
  ): Promise<CompanyBlockedStatusResponse> {
    return getJson<CompanyBlockedStatusResponse>(
      '/scripts/block_company/status',
      {
        schema_id: schemaId,
      },
    );
  },
  getCopyBaseDataPreview(
    targetSchema: string,
  ): Promise<CopyBaseDataPreviewResponse> {
    return getJson<CopyBaseDataPreviewResponse>(
      '/scripts/copy_base_data/preview',
      {target_schema: targetSchema},
    );
  },
  listApiKeyCompanyOptions(params?: {
    q?: string | null;
    limit?: number | null;
  }): Promise<{rows: CompanyOption[]}> {
    return getJson<{rows: CompanyOption[]}>(
      '/scripts/api_keys/company-options',
      params || {},
    );
  },
  listCompanyApiKeys(schemaId: string): Promise<{
    rows: ApiKeyRow[];
    total: number;
  }> {
    return getJson<{rows: ApiKeyRow[]; total: number}>('/scripts/api_keys', {
      schema_id: schemaId,
    });
  },
  getCompanyApiKeyToken(
    apiKeyId: string,
    schemaId: string,
  ): Promise<ApiKeyTokenResponse> {
    return getJson<ApiKeyTokenResponse>(`/scripts/api_keys/${apiKeyId}/token`, {
      schema_id: schemaId,
    });
  },
  listCompanyUsersWithoutApiKey(
    schemaId: string,
  ): Promise<{rows: ApiKeyUserOption[]}> {
    return getJson<{rows: ApiKeyUserOption[]}>(
      '/scripts/api_keys/users-without-key',
      {schema_id: schemaId},
    );
  },
  generateCompanyApiKey(
    payload: GenerateApiKeyPayload,
  ): Promise<GenerateApiKeyResponse> {
    return this.postJson<GenerateApiKeyResponse>(
      '/scripts/api_keys/generate',
      payload,
    );
  },
  patchCompanyApiKey(
    apiKeyId: string,
    payload: PatchApiKeyPayload,
  ): Promise<PatchApiKeyResponse> {
    return this.patchJson<PatchApiKeyResponse>(
      `/scripts/api_keys/${apiKeyId}`,
      payload,
    );
  },
  revokeCompanyApiKey(
    apiKeyId: string,
    schemaId: string,
  ): Promise<RevokeApiKeyResponse> {
    return this.deleteJson<RevokeApiKeyResponse>(
      `/scripts/api_keys/${apiKeyId}`,
      {schema_id: schemaId},
    );
  },
  forceRefreshWebuiVersion(
    schemaId: string,
  ): Promise<{ok: boolean; message: string}> {
    return getJson<{ok: boolean; message: string}>(
      '/scripts/force-refresh-webui-version',
      {
        schema_id: schemaId,
      },
    );
  },
  computeUsage(period: string): Promise<{message?: string}> {
    // FastAPI reads these arguments as query params, not a JSON body.
    return postWithQuery<{message?: string}>('/subscription/compute-usage', {
      period,
      include_all_companies: true,
    });
  },
  listUsageComputationDates(
    periods: string[],
  ): Promise<Array<{period: string; computed_at: string | null}>> {
    return getJson<Array<{period: string; computed_at: string | null}>>(
      '/subscription/computation-dates',
      {periods: periods.join(',')},
    );
  },
  async postJson<T>(path: string, body?: Record<string, any>): Promise<T> {
    const url = new URL(path.replace(/^\//, ''), getBaseUrl());
    const res = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: getBasicAuthHeader(),
      },
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(
        `Admin API error ${res.status}: ${text || res.statusText}`,
      );
    }
    return (await res.json()) as T;
  },
  async patchJson<T>(path: string, body?: Record<string, any>): Promise<T> {
    const url = new URL(path.replace(/^\//, ''), getBaseUrl());
    const res = await fetch(url.toString(), {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: getBasicAuthHeader(),
      },
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(
        `Admin API error ${res.status}: ${text || res.statusText}`,
      );
    }
    return (await res.json()) as T;
  },
  async deleteJson<T>(path: string, query?: Record<string, any>): Promise<T> {
    const url = new URL(path.replace(/^\//, ''), getBaseUrl());
    if (query) {
      Object.entries(query).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return;
        url.searchParams.set(k, String(v));
      });
    }
    const res = await fetch(url.toString(), {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: getBasicAuthHeader(),
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(
        `Admin API error ${res.status}: ${text || res.statusText}`,
      );
    }
    return (await res.json()) as T;
  },
  runScript<T = any>(
    runEndpoint: string,
    payload?: Record<string, any>,
  ): Promise<T> {
    return this.postJson<T>(runEndpoint, payload || {});
  },
  getRegisteredCompanies(params: {
    page: number;
    pageSize: number;
    sortBy?: string | null;
    sortDir?: 'asc' | 'desc' | null;
    filterText?: string | null;
    columnFilters?: string | null;
    blockedOnly?: boolean | null;
  }): Promise<RegisteredCompaniesResponse> {
    return getJson<RegisteredCompaniesResponse>(
      '/reports/registered-companies',
      params,
    );
  },
  getCompanyUsers(params: {
    page: number;
    pageSize: number;
    sortBy?: string | null;
    sortDir?: 'asc' | 'desc' | null;
    filterText?: string | null;
    columnFilters?: string | null;
    companyId?: string | null;
  }): Promise<CompanyUsersResponse> {
    return getJson<CompanyUsersResponse>('/reports/company-users', params);
  },
  listCompanyOptions(params?: {
    q?: string | null;
    limit?: number | null;
  }): Promise<{rows: CompanyOption[]}> {
    const requestedLimit = params?.limit ?? COMPANY_OPTIONS_MAX_LIMIT;
    const limit = Math.min(
      Math.max(1, Number(requestedLimit) || COMPANY_OPTIONS_MAX_LIMIT),
      COMPANY_OPTIONS_MAX_LIMIT,
    );
    return getJson<{rows: CompanyOption[]}>('/reports/company-options', {
      ...(params || {}),
      limit,
    });
  },
  getCompaniesNoLogin(params: {
    page: number;
    pageSize: number;
    sortBy?: string | null;
    sortDir?: 'asc' | 'desc' | null;
    filterText?: string | null;
    columnFilters?: string | null;
    weeks?: number | null;
    includeNever?: boolean | null;
    excludedEmails?: string | null;
    excludedCompanyIds?: string | null;
  }): Promise<CompaniesNoLoginResponse> {
    return getJson<CompaniesNoLoginResponse>(
      '/reports/companies-no-login',
      params,
    );
  },
  getRegisteredUsers(params: {
    page: number;
    pageSize: number;
    sortBy?: string | null;
    sortDir?: 'asc' | 'desc' | null;
    filterText?: string | null;
    columnFilters?: string | null;
  }): Promise<RegisteredUsersResponse> {
    return getJson<RegisteredUsersResponse>(
      '/reports/registered-users',
      params,
    );
  },
  getModuleUsageMonthly(params: {
    page: number;
    pageSize: number;
    sortBy?: string | null;
    sortDir?: 'asc' | 'desc' | null;
    filterText?: string | null;
    columnFilters?: string | null;
    month?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    companyId?: string | null;
    userId?: string | null;
    moduleType?: string | null;
  }): Promise<ModuleUsageMonthlyResponse> {
    return getJson<ModuleUsageMonthlyResponse>(
      '/reports/module-usage-monthly',
      params,
    );
  },
  getModuleUsageMonthlyKpis(params?: {
    month?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    companyId?: string | null;
    userId?: string | null;
    moduleType?: string | null;
  }): Promise<ModuleUsageMonthlyKpisResponse> {
    return getJson<ModuleUsageMonthlyKpisResponse>(
      '/reports/module-usage-monthly/kpis',
      params || {},
    );
  },
  listModuleUsageUsers(params?: {
    month?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    companyId?: string | null;
    limit?: number | null;
  }): Promise<{rows: SimpleOption[]}> {
    return getJson<{rows: SimpleOption[]}>(
      '/reports/module-usage-users',
      params || {},
    );
  },
  listModuleUsageModules(params?: {
    month?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    companyId?: string | null;
    userId?: string | null;
    limit?: number | null;
  }): Promise<{rows: SimpleOption[]}> {
    return getJson<{rows: SimpleOption[]}>(
      '/reports/module-usage-modules',
      params || {},
    );
  },
  getModuleUsageMonthlyDrilldown(params: {
    moduleType: string;
    month?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    companyId?: string | null;
    userId?: string | null;
    page: number;
    pageSize: number;
  }): Promise<ModuleUsageDrilldownResponse> {
    return getJson<ModuleUsageDrilldownResponse>(
      '/reports/module-usage-monthly/drilldown',
      params,
    );
  },
  exportRegisteredCompanies(params?: {
    sortBy?: string | null;
    sortDir?: 'asc' | 'desc' | null;
    filterText?: string | null;
    columnFilters?: string | null;
    maxRows?: number | null;
  }): Promise<Blob> {
    return getBlob(
      '/reports/registered-companies/export',
      params || {},
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
  },
  exportReportXlsx(
    exportEndpoint: string,
    params?: Record<string, any>,
  ): Promise<Blob> {
    return getBlob(
      exportEndpoint,
      params || {},
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
  },
  listCompanyServices(params?: {
    company_id?: string | null;
    date_from?: string | null;
    date_to?: string | null;
    period?: string | null;
    limit?: number | null;
  }): Promise<CompanyServiceRow[]> {
    return getJson<CompanyServiceRow[]>('/company_service/list', params || {});
  },
  getCompanyService(companyServiceId: string): Promise<CompanyServiceRow> {
    return getJson<CompanyServiceRow>(
      `/company_service/${encodeURIComponent(companyServiceId)}`,
    );
  },
  createCompanyService(
    payload: CompanyServiceCreatePayload,
  ): Promise<CompanyServiceRow> {
    return this.postJson<CompanyServiceRow>('/company_service/create', payload);
  },
  updateCompanyService(
    companyServiceId: string,
    payload: CompanyServicePatchPayload,
  ): Promise<CompanyServiceRow> {
    return patchJson<CompanyServiceRow>(
      `/company_service/${encodeURIComponent(companyServiceId)}`,
      payload,
    );
  },
  deleteCompanyService(companyServiceId: string): Promise<{deleted: boolean}> {
    return deleteJson<{deleted: boolean}>(
      `/company_service/${encodeURIComponent(companyServiceId)}`,
    );
  },
  getUsageHistory(params?: {
    months?: number | null;
    company_id?: string | null;
  }): Promise<UsageHistoryResponse> {
    return getJson<UsageHistoryResponse>(
      '/subscription/usage-history',
      params || {},
    );
  },
  getUsageForecast(params?: {
    company_id?: string | null;
  }): Promise<UsageForecastResponse> {
    return getJson<UsageForecastResponse>(
      '/subscription/usage-forecast',
      params || {},
    );
  },
  listLicences(): Promise<LicenceRow[]> {
    return getJson<LicenceRow[]>('/licence/list');
  },
  getLicenceDetails(licenceId: string): Promise<LicenceDetails> {
    return getJson<LicenceDetails>(`/licence/${encodeURIComponent(licenceId)}`);
  },
  addCompaniesToLicence(
    licenceId: string,
    payload: LicenceCompaniesPayload,
  ): Promise<{attempted: number}> {
    return this.postJson<{attempted: number}>(
      `/licence/${encodeURIComponent(licenceId)}/companies`,
      payload,
    );
  },
  listNotificationRecipientTypes(): Promise<NotificationRecipientType[]> {
    return getJson<NotificationRecipientType[]>(
      '/notifications/recipient-types',
    );
  },
  listNotificationVerificationStatuses(): Promise<
    {id: string; label: string}[]
  > {
    return getJson<{id: string; label: string}[]>(
      '/notifications/verification-statuses',
    );
  },
  previewNotificationBroadcast(
    params: NotificationBroadcastPreviewParams,
  ): Promise<NotificationBroadcastPreviewResponse> {
    return getJson<NotificationBroadcastPreviewResponse>(
      '/notifications/broadcasts/preview',
      params,
    );
  },
  previewNotificationRecipients(
    params: NotificationBroadcastPreviewParams,
  ): Promise<NotificationRecipientsListResponse> {
    return getJson<NotificationRecipientsListResponse>(
      '/notifications/broadcasts/preview/recipients',
      params,
    );
  },
  previewNotificationLocales(
    params: NotificationBroadcastPreviewParams,
  ): Promise<NotificationLocalesPreviewResponse> {
    return getJson<NotificationLocalesPreviewResponse>(
      '/notifications/broadcasts/preview/locales',
      params,
    );
  },
  resendNotificationBroadcast(
    broadcastId: string,
  ): Promise<NotificationBroadcastResendResponse> {
    return this.postJson<NotificationBroadcastResendResponse>(
      `/notifications/broadcasts/${broadcastId}/resend`,
      {},
    );
  },
  createNotificationBroadcast(
    body: NotificationBroadcastCreatePayload,
  ): Promise<NotificationBroadcastCreateResponse> {
    return this.postJson<NotificationBroadcastCreateResponse>(
      '/notifications/broadcasts',
      body,
    );
  },
  listNotificationBroadcasts(params?: {
    page?: number;
    pageSize?: number;
  }): Promise<NotificationBroadcastListResponse> {
    return getJson<NotificationBroadcastListResponse>(
      '/notifications/broadcasts',
      params || {},
    );
  },
  getNotificationBroadcast(
    broadcastId: string,
  ): Promise<{ok: boolean; broadcast: NotificationBroadcastRow}> {
    return getJson<{ok: boolean; broadcast: NotificationBroadcastRow}>(
      `/notifications/broadcasts/${broadcastId}`,
    );
  },
  getNotificationBroadcastDeliveries(
    broadcastId: string,
  ): Promise<NotificationBroadcastDeliveriesResponse> {
    return getJson<NotificationBroadcastDeliveriesResponse>(
      `/notifications/broadcasts/${broadcastId}/deliveries`,
    );
  },
  sendNotificationTest(
    body: NotificationBroadcastTestPayload,
  ): Promise<{ok: boolean; message?: string}> {
    return this.postJson<{ok: boolean; message?: string}>(
      '/notifications/broadcasts/test',
      body,
    );
  },
};

export type NotificationRecipientType = {
  id: string;
  label: string;
  requiresCompany: boolean;
  requiresUser: boolean;
  supportsCompanyFilters: boolean;
};

export type NotificationBroadcastPreviewParams = {
  recipientType: string;
  companyId?: string | null;
  userId?: string | null;
  companyVerificationStatuses?: string | null;
  companyActiveOnly?: boolean;
};

export type NotificationBroadcastPreviewResponse = {
  ok: boolean;
  recipient_count: number;
  unique_user_count: number;
  unique_email_count: number;
  company_filter_warning?: string | null;
};

export type NotificationRecipientRow = {
  user_id: string;
  name: string;
  email?: string | null;
  admin: boolean;
  company_id?: string | null;
  company_designation?: string | null;
  company_verification_status?: string | null;
};

export type NotificationRecipientsListResponse = {
  ok: boolean;
  rows: NotificationRecipientRow[];
  total: number;
  truncated: boolean;
};

export type NotificationLocalePreview = {
  id: string;
  label?: string;
  user_count?: number;
};

export type NotificationLocalesPreviewResponse = {
  ok: boolean;
  locales: NotificationLocalePreview[];
  default_locale?: string;
};

export type NotificationBroadcastResendResponse = {
  ok: boolean;
  id: string;
  resent_from_id: string;
  status: string;
  recipient_count: number;
  message?: string;
};

export type NotificationBroadcastCreatePayload = {
  recipient_type: string;
  company_id?: string | null;
  user_id?: string | null;
  title: string;
  body?: string;
  body_by_locale?: Record<string, string>;
  body_is_html?: boolean;
  send_email?: boolean;
  email_subject?: string | null;
  company_verification_statuses?: string[] | null;
  company_active_only?: boolean;
  show?: boolean;
  ttl?: number;
};

export type NotificationBroadcastCreateResponse = {
  ok: boolean;
  id: string;
  status: string;
  recipient_count: number;
  company_filter_warning?: string | null;
  message?: string;
};

export type NotificationBroadcastRow = {
  id: string;
  status: string;
  recipient_type: string;
  company_id?: string | null;
  user_id?: string | null;
  title: string;
  send_email?: boolean;
  company_verification_statuses?: string[] | null;
  company_active_only?: boolean;
  created_at?: string;
  created_by?: string | null;
  processed_at?: string | null;
  recipient_count?: number;
  sent_count?: number;
  failed_count?: number;
  error_message?: string | null;
  company_designation?: string | null;
  user_email?: string | null;
  user_first_name?: string | null;
  user_last_name?: string | null;
};

export type NotificationBroadcastListResponse = {
  rows: NotificationBroadcastRow[];
  total: number;
  page: number;
  pageSize: number;
};

export type NotificationBroadcastTestPayload = {
  recipient_email: string;
  title: string;
  body?: string;
  body_is_html?: boolean;
  send_email?: boolean;
  email_subject?: string | null;
  locale?: string;
};

export type NotificationBroadcastDeliveryRow = {
  user_id: string;
  company_id?: string | null;
  email?: string | null;
  name?: string;
  admin?: boolean | null;
  company_designation?: string | null;
  company_verification_status?: string | null;
  delivery_status: string;
  email_status?: string;
  delivery_channel?: string;
};

export type NotificationBroadcastDeliveriesResponse = {
  ok: boolean;
  rows: NotificationBroadcastDeliveryRow[];
  total: number;
  tracked: boolean;
};
